// เทียบชีวิตนี้กับผู้เล่นทั้งหมดที่เคยเกิดเป็นชนิดเดียวกัน (สถิติรวม docs/PLAN.md เฟส 4)

import { useEffect, useState } from "react";
import { summarize } from "../../engine/life";
import { displayName, formatAge } from "../../engine/text";
import type { LifeState } from "../../engine/types";
import type { StatsResponse } from "../../shared/stats";
import { fetchStats } from "../api";
import { fmt } from "../names";

interface Props {
  life: LifeState;
  sent: Promise<void>; // รอส่งชีวิตนี้ก่อนค่อยอ่าน
}

const pct = (n: number, d: number) => `${Math.round((n / Math.max(1, d)) * 100)}%`;

export function SpeciesStatsPanel({ life, sent }: Props) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    sent
      .then(() => fetchStats(life.species.id))
      .then((s) => live && setStats(s))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [life.species.id, sent]);

  if (failed) return null;
  if (!stats) return <p className="small muted">กำลังโหลดสถิติของผู้เล่นทั้งหมด…</p>;

  const sp = stats.species;
  const mine = summarize(life);
  const name = displayName(life.species);
  let compare: string | null = null;
  if (sp && sp.lives > 1) {
    const avg = sp.ageDays / sp.lives;
    // อายุที่ส่งไปถูกปัดทศนิยมหนึ่งตำแหน่ง จึงเผื่อส่วนต่างไว้ครึ่งวัน
    if (mine.ageDays + 0.5 >= sp.bestAgeDays) compare = `ชีวิตนี้อายุยืนที่สุดเท่าที่ผู้เล่นทุกคนเคยเป็น${name}มา`;
    else if (mine.ageDays > avg) compare = `ชีวิตนี้อายุยืนกว่าค่าเฉลี่ยของ${name}ทั้งหมด (${formatAge(avg)})`;
    else compare = `อายุเฉลี่ยของ${name}ที่ผู้เล่นทุกคนเคยเป็นคือ ${formatAge(avg)}`;
  }

  return (
    <section className="card global-stats">
      <p className="small muted">เทียบกับผู้เล่นทั้งหมด</p>
      {sp ? (
        <>
          <p>
            ผู้เล่นเคยเกิดเป็น{name}มาแล้ว <b>{fmt(sp.lives)}</b> ชีวิต · รอดจนโต {pct(sp.adults, sp.lives)}
          </p>
          <p className="small">
            อายุยืนที่สุด {formatAge(sp.bestAgeDays)} · ลูกมากที่สุด {fmt(sp.bestOffspring)}
            {sp.maxGeneration > 1 && ` · สายตระกูลยาวที่สุด ${fmt(sp.maxGeneration)} รุ่น`}
          </p>
          {compare && <p className="small">{compare}</p>}
        </>
      ) : (
        <p>คุณเป็นคนแรกที่ได้เกิดเป็น{name}</p>
      )}
      <p className="small muted">ผู้เล่นทุกคนใช้ชีวิตรวมกันมาแล้ว {fmt(stats.global.lives)} ชีวิต · ข้อมูลอัปเดตทุกราว 1 นาที</p>
    </section>
  );
}
